import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronUp,
  Download,
  FileSpreadsheet,
  Globe,
  MapPinned,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { MultiSelect } from "@/components/ui/MultiSelect";
import { useAuthStore } from "@/store/auth";
import { triggerBrowserDownload } from "@/utils/download";
import { hasOnlyMinimalDlc01 } from "@/utils/dlc";
import {
  EmptyState,
  PageHeader,
  ParcelStatusBadge,
  parcelDotColor,
  TableShell,
  Th,
} from "../components/ui";
import { startGlobalParcelsXlsx } from "../api";
import { GlobalKwPanel } from "../components/KwTab";
import { useGlobalParcels, useParcelExportFacets, useTaskPolling } from "../hooks";
import { apiErr, fmtArea } from "../utils";
import type { GlobalParcelRow, ParcelExportFilters, ParcelExportResult } from "../types";

// Scalar project filters — the same single-choice dropdowns as the portfolio / export modal.
const DEADLINE_OPTS = [
  { value: "", label: "Termin: wszystkie" },
  { value: "overdue", label: "Po terminie" },
  { value: "soon", label: "≤ 14 dni" },
  { value: "approaching", label: "≤ 30 dni" },
  { value: "ok", label: "W terminie" },
  { value: "none", label: "Brak terminu" },
];
const PROGRESS_OPTS = [
  { value: "", label: "Postęp: wszystkie" },
  { value: "not_started", label: "Nierozpoczęte (0%)" },
  { value: "in_progress", label: "W toku" },
  { value: "done", label: "Zakończone (100%)" },
  { value: "problem", label: "Z problemem na działkach" },
];
const AGREEMENT_OPTS = [
  { value: "", label: "Uzgodnienia: wszystkie" },
  { value: "open", label: "Otwarte" },
  { value: "closed", label: "Zamknięte" },
];
const ROUTE_OPTS = [
  { value: "", label: "Trasa: wszystkie" },
  { value: "yes", label: "W aktualnej trasie" },
  { value: "no", label: "Poza aktualną trasą" },
];
const MPZP_ORDERED_OPTS = [
  { value: "", label: "Wypis MPZP: wszystkie" },
  { value: "yes", label: "Z podpiętym wypisem MPZP" },
  { value: "no", label: "Bez wypisu MPZP" },
];
// Wypis z rejestru gruntów (EGiB) podpięty do działki — w UI checkbox (tylko "yes"),
// ale backend przyjmuje też "no"; lista opcji zasila chipy.
const WYPIS_ATTACHED_OPTS = [
  { value: "", label: "Wypis: wszystkie" },
  { value: "yes", label: "Z podpiętym wypisem z rejestru gruntów" },
  { value: "no", label: "Bez wypisu z rejestru gruntów" },
];
const KONCEPCJA_OPTS = [
  { value: "", label: "Koncepcja: wszystkie" },
  { value: "yes", label: "Koncepcja: tak" },
  { value: "no", label: "Koncepcja: nie" },
];
const KONCEPCJA_EOP_OPTS = [
  { value: "", label: "Koncepcja uzgodniona z EOP: wszystkie" },
  { value: "yes", label: "Koncepcja uzgodniona z EOP: tak" },
  { value: "no", label: "Koncepcja uzgodniona z EOP: nie" },
];
const PAGE_SIZES = [25, 50, 100, 200];
const CHIPS_MAX_H = 132;

// Server-side sort keys accepted by parcels/global-list/ (see parcel_export.list_ordering).
type SortKey =
  | "teryt" | "project" | "county" | "commune" | "obreb" | "area"
  | "status" | "land_use" | "ownership_code";
interface SortState {
  key: SortKey | null;
  dir: "asc" | "desc";
}

// v2: default zmienił się na "moje + w aktualnej trasie" — nowy klucz, żeby dotychczasowy
// zapisany stan nie przykrył nowego defaultu.
const FILTERS_STORAGE_KEY = "powerProjects.globalParcels.filters.v2";

// Default view: my projects, parcels on the current route.
const defaultFilters = (meId: string | undefined): ParcelExportFilters => ({
  ...(meId ? { coordinator_ids: [meId] } : {}),
  in_current_route: "yes",
});

interface StoredState {
  filters: ParcelExportFilters;
  sort: SortState;
  pageSize: number;
}

// Minimalny dostęp do DLC_01 trzyma filtry pod osobnym kluczem — nie dziedziczy
// „Prowadzący: ja" / „na trasie" z pełnego widoku na tym samym komputerze.
const storageKey = (minimal: boolean) =>
  minimal ? `${FILTERS_STORAGE_KEY}.minimal` : FILTERS_STORAGE_KEY;

function readStored(minimal: boolean): StoredState | null {
  try {
    const raw = localStorage.getItem(storageKey(minimal));
    return raw ? (JSON.parse(raw) as StoredState) : null;
  } catch {
    return null;
  }
}

function writeStored(minimal: boolean, state: StoredState) {
  try {
    localStorage.setItem(storageKey(minimal), JSON.stringify(state));
  } catch {
    // ignore write failures (private mode / quota)
  }
}

// Filter keys → the chip label shown when the panel is collapsed.
const LIST_KEY_LABELS: Record<string, string> = {
  project_ids: "Przedsięwzięcie",
  coordinator_ids: "Prowadzący",
  branch_ids: "Oddział",
  client_ids: "Klient",
  statuses: "Status",
  access_modes: "Tryb dostępu",
  location_categories: "Kategoria",
  voivodeships: "Województwo",
  counties: "Powiat",
  communes: "Gmina",
  land_uses: "Rodzaj użytku",
  soil_classes: "Klasa bonitacyjna",
};
const SCALAR_KEY_OPTS: Record<string, { value: string; label: string }[] | null> = {
  search: null,
  owner_search: null,
  deadline_status: DEADLINE_OPTS,
  progress: PROGRESS_OPTS,
  agreements: AGREEMENT_OPTS,
  in_current_route: ROUTE_OPTS,
  mpzp_ordered: MPZP_ORDERED_OPTS,
  wypis_attached: WYPIS_ATTACHED_OPTS,
  concept: KONCEPCJA_OPTS,
  concept_agreed_eop: KONCEPCJA_EOP_OPTS,
};
const SCALAR_KEY_LABELS: Record<string, string> = {
  search: "Projekt",
  owner_search: "Właściciel",
  deadline_status: "Termin",
  progress: "Postęp",
  agreements: "Uzgodnienia",
  in_current_route: "Trasa",
  mpzp_ordered: "Wypis MPZP",
  wypis_attached: "Wypis",
  concept: "Koncepcja",
  concept_agreed_eop: "Koncepcja uzgodniona z EOP",
};

function countActiveFilters(f: ParcelExportFilters): number {
  const lists = Object.keys(LIST_KEY_LABELS).filter(
    (k) => ((f as Record<string, unknown>)[k] as string[] | undefined)?.length,
  );
  const scalars = Object.keys(SCALAR_KEY_LABELS).filter(
    (k) => !!((f as Record<string, unknown>)[k] as string | undefined)?.trim(),
  );
  return lists.length + scalars.length + (f.parcel_search?.trim() ? 1 : 0);
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="section-label">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function SortHeader({
  label,
  col,
  sort,
  onSort,
  className,
}: {
  label: string;
  col: SortKey;
  sort: SortState;
  onSort: (k: SortKey) => void;
  className?: string;
}) {
  const active = sort.key === col;
  return (
    <Th className={className}>
      <button
        type="button"
        onClick={() => onSort(col)}
        className="inline-flex items-center gap-1 uppercase tracking-widest hover:text-[var(--color-text-primary)] transition-colors"
        title="Sortuj"
      >
        {label}
        <span
          className={
            active
              ? "text-[var(--color-primary)]"
              : "text-[var(--color-text-muted)] opacity-40"
          }
        >
          {active ? (sort.dir === "asc" ? "▲" : "▼") : "↕"}
        </span>
      </button>
    </Th>
  );
}

// Klasoużytek: wypis value (authoritative) + muted geoportal cross-check.
function LandClassCell({ row }: { row: GlobalParcelRow }) {
  const wypis = [row.land_use, row.soil_class].filter(Boolean).join(" ");
  return (
    <div className="space-y-0.5">
      <div className="text-[var(--color-text-primary)]">{wypis || "—"}</div>
      {row.geoportal_land_use && (
        <div
          className="flex items-center gap-1 text-[10px] text-[var(--color-text-muted)]"
          title="Klasoużytek wg geoportalu (weryfikacja)"
        >
          <Globe size={10} /> {row.geoportal_land_use}
        </div>
      )}
    </div>
  );
}

function OwnersCell({ row }: { row: GlobalParcelRow }) {
  const names = row.owner_names;
  if (!names.length) return <span className="text-[var(--color-text-muted)]">—</span>;
  const shown = names.slice(0, 2);
  return (
    <div title={names.join("\n")}>
      {shown.map((n) => (
        <div key={n} className="truncate max-w-[160px]">{n}</div>
      ))}
      {names.length > shown.length && (
        <div className="text-[10px] text-[var(--color-text-muted)]">
          +{names.length - shown.length} więcej
        </div>
      )}
    </div>
  );
}

// Removable summary chip for one active filter — visible when the panel is collapsed,
// so "0 działek" is never a mystery.
function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 max-w-[240px] border border-[var(--color-border-strong)] bg-[var(--color-bg-surface)] px-2 py-0.5 text-[11px] font-mono text-[var(--color-text-secondary)]">
      <span className="truncate">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
        title="Usuń filtr"
      >
        <X size={11} />
      </button>
    </span>
  );
}

export function GlobalParcelsPage() {
  const me = useAuthStore((s) => s.user);
  // Minimalny dostęp do DLC_01: sama lista — nazwy i filtry tak, wejście w projekt nie
  // (API szczegółów i tak odpowiada 403).
  const minimal = hasOnlyMinimalDlc01(me?.accessible_dlc_modules);

  // First visit (nothing stored) defaults to "my projects" — koordynator = current user.
  const initial = useMemo<StoredState>(() => {
    const stored = readStored(minimal);
    if (stored) {
      return {
        filters: stored.filters ?? {},
        sort: stored.sort ?? { key: null, dir: "asc" },
        pageSize: stored.pageSize ?? 50,
      };
    }
    return {
      // Minimalny dostęp: bez domyślnych filtrów (ani prowadzącego, ani trasy).
      filters: minimal ? {} : defaultFilters(me?.id),
      sort: { key: null, dir: "asc" },
      pageSize: 50,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [f, setF] = useState<ParcelExportFilters>(initial.filters);
  const [sort, setSort] = useState<SortState>(initial.sort);
  const [pageSize, setPageSize] = useState<number>(initial.pageSize);
  const [page, setPage] = useState(1);
  const [panelOpen, setPanelOpen] = useState(false); // filters collapsed by default
  // Widok strony: pełna lista działek albo zestawienie ksiąg wieczystych (KW).
  const [view, setView] = useState<"lista" | "kw">("lista");

  const upd = (patch: Partial<ParcelExportFilters>) => setF((s) => ({ ...s, ...patch }));
  const filtersKey = JSON.stringify(f);
  const activeCount = useMemo(() => countActiveFilters(f), [filtersKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    writeStored(minimal, { filters: f, sort, pageSize });
  }, [filtersKey, sort, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounce: typing in a search box shouldn't fire a request per keystroke.
  const [debounced, setDebounced] = useState<ParcelExportFilters>(initial.filters);
  useEffect(() => {
    const id = setTimeout(() => {
      setDebounced(f);
      setPage(1);
    }, 350);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  // Facets: full list keeps chip labels stable; live list cascades with the other filters.
  // Fetched lazily — only once the panel is opened (or a chip needs an entity label).
  const facetsNeeded = panelOpen || activeCount > 0;
  const { data: fullFacets, isLoading: facetsLoading } = useParcelExportFacets({}, facetsNeeded);
  const { data: liveFacets } = useParcelExportFacets(debounced, panelOpen);
  const facets = liveFacets ?? fullFacets;

  const entityLabels = useMemo(() => {
    const m = new Map<string, string>();
    if (fullFacets) {
      [...fullFacets.projects, ...fullFacets.coordinators, ...fullFacets.branches, ...fullFacets.clients]
        .forEach((e) => m.set(e.id, e.name));
    }
    return m;
  }, [fullFacets]);

  const entityOptions = (list: { id: string; name: string }[] | undefined, selected: string[]) => {
    const opts = (list ?? []).map((e) => ({ value: e.id, label: e.name }));
    const present = new Set(opts.map((o) => o.value));
    selected.forEach((id) => {
      if (!present.has(id)) opts.push({ value: id, label: entityLabels.get(id) ?? id });
    });
    return opts;
  };
  const stringOptions = (list: string[] | undefined, selected: string[]) => {
    const opts = (list ?? []).map((v) => ({ value: v, label: v }));
    const present = new Set(opts.map((o) => o.value));
    selected.forEach((v) => {
      if (!present.has(v)) opts.push({ value: v, label: v });
    });
    return opts;
  };

  // Enum value → label (statuses / access modes / location categories) for chips.
  const enumLabels = useMemo(() => {
    const m = new Map<string, string>();
    (fullFacets?.statuses ?? []).forEach((o) => m.set(`statuses:${o.value}`, o.label));
    (fullFacets?.access_modes ?? []).forEach((o) => m.set(`access_modes:${o.value}`, o.label));
    (fullFacets?.location_categories ?? []).forEach((o) =>
      m.set(`location_categories:${o.value}`, o.label),
    );
    return m;
  }, [fullFacets]);

  // One chip per active filter (search boxes have their own visible inputs → no chip
  // for parcel_search, which lives in the always-visible toolbar).
  const chips = useMemo(() => {
    const out: { key: string; label: string; remove: Partial<ParcelExportFilters> }[] = [];
    const fr = f as Record<string, unknown>;
    for (const [key, label] of Object.entries(LIST_KEY_LABELS)) {
      const vals = fr[key] as string[] | undefined;
      if (!vals?.length) continue;
      const isMine =
        key === "coordinator_ids" && vals.length === 1 && !!me?.id && vals[0] === me.id;
      const names = vals.map(
        (v) =>
          (key === "statuses" || key === "access_modes" || key === "location_categories"
            ? enumLabels.get(`${key}:${v}`)
            : entityLabels.get(v)) ?? v,
      );
      out.push({
        key,
        label: isMine ? "Prowadzący: ja" : `${label}: ${names.join(", ")}`,
        remove: { [key]: [] },
      });
    }
    for (const [key, label] of Object.entries(SCALAR_KEY_LABELS)) {
      const val = (fr[key] as string | undefined)?.trim();
      if (!val) continue;
      const opts = SCALAR_KEY_OPTS[key];
      const display = opts ? (opts.find((o) => o.value === val)?.label ?? val) : `${label}: ${val}`;
      out.push({ key, label: display, remove: { [key]: "" } });
    }
    return out;
  }, [filtersKey, entityLabels, enumLabels, me?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const ordering = sort.key ? `${sort.dir === "desc" ? "-" : ""}${sort.key}` : "";
  const list = useGlobalParcels({
    filters: debounced,
    page,
    page_size: pageSize,
    ordering,
  });
  const data = list.data;

  const onSort = (k: SortKey) =>
    setSort((s) =>
      s.key === k ? { key: k, dir: s.dir === "asc" ? "desc" : "asc" } : { key: k, dir: "asc" },
    );

  const clearAll = () => setF({});
  // Back to the default view: my projects + parcels on the current route.
  const onlyMine = () => setF(defaultFilters(me?.id));

  // ── eksport XLSX (zadanie w tle) — WSZYSTKIE odfiltrowane działki, nie tylko strona ──
  const [exportTaskId, setExportTaskId] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const downloadedRef = useRef<string | null>(null);

  const exportMut = useMutation({
    mutationFn: (opts: { variant: "default" | "address"; file_format?: "xlsx" | "csv" }) =>
      startGlobalParcelsXlsx({ filters: debounced, ordering, ...opts }),
    onSuccess: (r) => setExportTaskId(r.task_id),
    onError: (e) => setExportError(apiErr(e)),
  });
  const exportTask = useTaskPolling(exportTaskId);
  const exportStatus = exportTask.data?.status;
  const exporting =
    exportMut.isPending ||
    (!!exportTaskId && !["success", "failure", "cancelled"].includes(exportStatus ?? ""));
  const exportResult =
    exportStatus === "success" ? (exportTask.data?.result as ParcelExportResult | null) : null;

  // Auto-download once the file is ready (each file url only once).
  useEffect(() => {
    if (exportResult?.file_url && downloadedRef.current !== exportResult.file_url) {
      downloadedRef.current = exportResult.file_url;
      triggerBrowserDownload(exportResult.file_url, exportResult.filename);
    }
  }, [exportResult]);

  const startExport = (
    variant: "default" | "address" = "default",
    file_format: "xlsx" | "csv" = "xlsx",
  ) => {
    setExportError(null);
    setExportTaskId(null);
    exportMut.mutate({ variant, file_format });
  };

  return (
    <div>
      <PageHeader
        title="Działki"
        subtitle="Wszystkie działki ze wszystkich projektów ZE — filtruj i wyszukuj globalnie"
      />

      {/* ── toolbar (always visible) ── */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <input
          placeholder="Szukaj: nr działki, obręb, KW…"
          className="input-base w-full sm:w-auto sm:flex-1 sm:min-w-[220px] sm:max-w-md"
          value={f.parcel_search ?? ""}
          onChange={(e) => upd({ parcel_search: e.target.value })}
        />
        <Button
          variant={panelOpen ? "secondary" : "ghost"}
          size="sm"
          onClick={() => setPanelOpen((o) => !o)}
        >
          <SlidersHorizontal size={13} />
          Filtry{activeCount > 0 ? ` (${activeCount})` : ""}
          {panelOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </Button>
        {!minimal && (
          <Button variant="ghost" size="sm" onClick={onlyMine}>
            Moje projekty
          </Button>
        )}
        {/* Widok: pełna lista działek albo zestawienie ksiąg wieczystych (te same filtry). */}
        <div className="flex items-center gap-1 ml-1">
          <Button
            variant={view === "lista" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setView("lista")}
          >
            Lista
          </Button>
          <Button
            variant={view === "kw" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setView("kw")}
          >
            KW
          </Button>
        </div>
        {activeCount > 0 && (
          <Button variant="ghost" size="sm" onClick={clearAll}>
            Wyczyść filtry
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => startExport("default")}
          disabled={exporting || !data || data.count === 0}
          loading={exporting}
          title="Pobierz wszystkie odfiltrowane działki (nie tylko bieżącą stronę) jako arkusz Excel — plik tworzy się w tle"
        >
          <FileSpreadsheet size={13} /> Pobierz działki (.xlsx)
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => startExport("address")}
          disabled={exporting || !data || data.count === 0}
          loading={exporting}
          title="Wykaz działek z adresami: Identyfikator (TERYT), Obręb ewidencyjny, Numer działki ewidencyjnej, Województwo, Powiat, Gmina, Miejscowość, Ulica, Nr domu, Kod pocztowy"
        >
          <FileSpreadsheet size={13} /> Wykaz adresowy (.xlsx)
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => startExport("address", "csv")}
          disabled={exporting || !data || data.count === 0}
          loading={exporting}
          title="Ten sam wykaz adresowy jako CSV (średniki, UTF-8 z BOM — otwiera się poprawnie w Excelu)"
        >
          <FileSpreadsheet size={13} /> Wykaz adresowy (.csv)
        </Button>
        <span className="section-label ml-auto whitespace-nowrap">
          {list.isFetching && !data ? "…" : `${data?.count ?? 0} działek`}
        </span>
      </div>

      {/* ── stan eksportu XLSX ── */}
      {(exporting || exportError || exportStatus === "failure" || exportResult) && (
        <div className="mb-2 text-[11px] font-mono">
          {exportError || exportStatus === "failure" ? (
            <span className="text-[var(--color-primary)]">
              {exportError ?? "Eksport XLSX nieudany — spróbuj ponownie."}
            </span>
          ) : exportResult ? (
            exportResult.file_url && (
              <a
                href={exportResult.file_url}
                download={exportResult.filename}
                className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline"
              >
                <Download size={11} /> Plik gotowy — pobieranie rozpoczęte. Pobierz ponownie (
                {exportResult.count} działek)
              </a>
            )
          ) : (
            <span className="text-[var(--color-text-muted)]">
              Tworzenie pliku XLSX w tle — pobieranie rozpocznie się automatycznie…
            </span>
          )}
        </div>
      )}

      {/* ── active-filter chips (context when the panel is collapsed) ── */}
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-3">
          {chips.map((c) => (
            <FilterChip key={c.key} label={c.label} onRemove={() => upd(c.remove)} />
          ))}
        </div>
      )}

      {/* ── expandable filter panel ── */}
      {panelOpen && (
        <div className="card p-4 mb-4 space-y-4 text-sm">
          {facetsLoading ? (
            <div className="py-6 flex justify-center"><Spinner /></div>
          ) : (
            <>
              <div>
                <div className="section-label mb-2 text-[var(--color-text-primary)]">Projekt</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <Field label="Szukaj projektu">
                    <input
                      className="input-base w-full"
                      placeholder="Przedsięwzięcie, klient, nr umowy…"
                      value={f.search ?? ""}
                      onChange={(e) => upd({ search: e.target.value })}
                    />
                  </Field>
                  <Field label="Przedsięwzięcie">
                    <MultiSelect
                      options={entityOptions(facets?.projects, f.project_ids ?? [])}
                      value={f.project_ids ?? []}
                      onChange={(v) => upd({ project_ids: v })}
                      chipsMaxHeight={CHIPS_MAX_H}
                    />
                  </Field>
                  <Field label="Prowadzący">
                    <MultiSelect
                      options={entityOptions(facets?.coordinators, f.coordinator_ids ?? [])}
                      value={f.coordinator_ids ?? []}
                      onChange={(v) => upd({ coordinator_ids: v })}
                      placeholder="— wszyscy —"
                      chipsMaxHeight={CHIPS_MAX_H}
                    />
                  </Field>
                  <Field label="Oddział">
                    <MultiSelect
                      options={entityOptions(facets?.branches, f.branch_ids ?? [])}
                      value={f.branch_ids ?? []}
                      onChange={(v) => upd({ branch_ids: v })}
                      chipsMaxHeight={CHIPS_MAX_H}
                    />
                  </Field>
                  <Field label="Klient">
                    <MultiSelect
                      options={entityOptions(facets?.clients, f.client_ids ?? [])}
                      value={f.client_ids ?? []}
                      onChange={(v) => upd({ client_ids: v })}
                      placeholder="— wszyscy —"
                      chipsMaxHeight={CHIPS_MAX_H}
                    />
                  </Field>
                  <Field label="Termin">
                    <select
                      className="input-base w-full"
                      value={f.deadline_status ?? ""}
                      onChange={(e) => upd({ deadline_status: e.target.value })}
                    >
                      {DEADLINE_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Postęp">
                    <select
                      className="input-base w-full"
                      value={f.progress ?? ""}
                      onChange={(e) => upd({ progress: e.target.value })}
                    >
                      {PROGRESS_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Uzgodnienia">
                    <select
                      className="input-base w-full"
                      value={f.agreements ?? ""}
                      onChange={(e) => upd({ agreements: e.target.value })}
                    >
                      {AGREEMENT_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Koncepcja">
                    <select
                      className="input-base w-full"
                      value={f.concept ?? ""}
                      onChange={(e) => upd({ concept: e.target.value })}
                      title="Tak = koncepcja wysłana lub uzgodniona (kamień „Koncepcja” / projekt techniczny)"
                    >
                      {KONCEPCJA_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Koncepcja uzgodniona z EOP">
                    <select
                      className="input-base w-full"
                      value={f.concept_agreed_eop ?? ""}
                      onChange={(e) => upd({ concept_agreed_eop: e.target.value })}
                      title="Tak = uzgodnienie koncepcji z EOP zakończone (kamień „Uzgodnienie koncepcji z EOP” / projekt techniczny)"
                    >
                      {KONCEPCJA_EOP_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </Field>
                </div>
              </div>

              <div className="border-t border-[var(--color-border-muted)] pt-3">
                <div className="section-label mb-2 text-[var(--color-text-primary)]">Działka</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <Field label="Właściciel">
                    <input
                      className="input-base w-full"
                      placeholder="Nazwisko / nazwa właściciela…"
                      value={f.owner_search ?? ""}
                      onChange={(e) => upd({ owner_search: e.target.value })}
                    />
                  </Field>
                  <Field label="Status">
                    <MultiSelect
                      options={(facets?.statuses ?? []).map((s) => ({ value: s.value, label: s.label }))}
                      value={f.statuses ?? []}
                      onChange={(v) => upd({ statuses: v })}
                      chipsMaxHeight={CHIPS_MAX_H}
                    />
                  </Field>
                  <Field label="Tryb pozyskania dostępu">
                    <MultiSelect
                      options={(facets?.access_modes ?? []).map((s) => ({ value: s.value, label: s.label }))}
                      value={f.access_modes ?? []}
                      onChange={(v) => upd({ access_modes: v })}
                      chipsMaxHeight={CHIPS_MAX_H}
                    />
                  </Field>
                  <Field label="Kategoria uzgodnienia">
                    <MultiSelect
                      options={(facets?.location_categories ?? []).map((s) => ({ value: s.value, label: s.label }))}
                      value={f.location_categories ?? []}
                      onChange={(v) => upd({ location_categories: v })}
                      chipsMaxHeight={CHIPS_MAX_H}
                    />
                  </Field>
                  <Field label="Trasa">
                    <select
                      className="input-base w-full"
                      value={f.in_current_route ?? ""}
                      onChange={(e) => upd({ in_current_route: e.target.value })}
                    >
                      {ROUTE_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Zamówiony wypis MPZP">
                    <select
                      className="input-base w-full"
                      value={f.mpzp_ordered ?? ""}
                      onChange={(e) => upd({ mpzp_ordered: e.target.value })}
                    >
                      {MPZP_ORDERED_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </Field>
                  {/* Field renders a <label>, so the row is a plain span — clicking the
                      caption still toggles the checkbox via the outer label. */}
                  <Field label="Wypis z rejestru gruntów">
                    <span className="flex items-center gap-2 cursor-pointer text-xs h-[34px]">
                      <input
                        type="checkbox"
                        checked={f.wypis_attached === "yes"}
                        onChange={(e) => upd({ wypis_attached: e.target.checked ? "yes" : "" })}
                      />
                      <span>Z podpiętym wypisem</span>
                    </span>
                  </Field>
                  <Field label="Rodzaj użytku">
                    <MultiSelect
                      options={stringOptions(facets?.land_uses, f.land_uses ?? [])}
                      value={f.land_uses ?? []}
                      onChange={(v) => upd({ land_uses: v })}
                      chipsMaxHeight={CHIPS_MAX_H}
                    />
                  </Field>
                  <Field label="Klasa bonitacyjna">
                    <MultiSelect
                      options={stringOptions(facets?.soil_classes, f.soil_classes ?? [])}
                      value={f.soil_classes ?? []}
                      onChange={(v) => upd({ soil_classes: v })}
                      chipsMaxHeight={CHIPS_MAX_H}
                    />
                  </Field>
                </div>
              </div>

              <div className="border-t border-[var(--color-border-muted)] pt-3">
                <div className="section-label mb-2 text-[var(--color-text-primary)]">Położenie</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <Field label="Województwo">
                    <MultiSelect
                      options={stringOptions(facets?.voivodeships, f.voivodeships ?? [])}
                      value={f.voivodeships ?? []}
                      onChange={(v) => upd({ voivodeships: v })}
                      chipsMaxHeight={CHIPS_MAX_H}
                    />
                  </Field>
                  <Field label="Powiat">
                    <MultiSelect
                      options={stringOptions(facets?.counties, f.counties ?? [])}
                      value={f.counties ?? []}
                      onChange={(v) => upd({ counties: v })}
                      chipsMaxHeight={CHIPS_MAX_H}
                    />
                  </Field>
                  <Field label="Gmina">
                    <MultiSelect
                      options={stringOptions(facets?.communes, f.communes ?? [])}
                      value={f.communes ?? []}
                      onChange={(v) => upd({ communes: v })}
                      chipsMaxHeight={CHIPS_MAX_H}
                    />
                  </Field>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── wyniki ── */}
      {view === "kw" ? (
        <GlobalKwPanel filters={debounced} readOnly={minimal} />
      ) : list.isLoading ? (
        <div className="py-12 flex justify-center"><Spinner /></div>
      ) : !data || data.count === 0 ? (
        <EmptyState>
          <MapPinned size={28} className="mx-auto mb-2 opacity-50" />
          <div>Brak działek dla wybranych filtrów</div>
          <div className="mt-1 text-[11px] normal-case tracking-normal">
            Zmień kryteria albo wyczyść filtry, aby zobaczyć wszystkie działki.
          </div>
        </EmptyState>
      ) : (
        <>
          <TableShell>
            <thead>
              <tr>
                <SortHeader label="Nr działki" col="teryt" sort={sort} onSort={onSort} />
                <SortHeader label="Przedsięwzięcie" col="project" sort={sort} onSort={onSort} />
                <Th>Prowadzący</Th>
                <SortHeader label="Powiat" col="county" sort={sort} onSort={onSort} />
                <SortHeader label="Gmina" col="commune" sort={sort} onSort={onSort} />
                <SortHeader label="Obręb" col="obreb" sort={sort} onSort={onSort} />
                <SortHeader label="Pow." col="area" sort={sort} onSort={onSort} className="text-right" />
                <SortHeader label="Klasoużytek" col="land_use" sort={sort} onSort={onSort} />
                <SortHeader label="Kod wł." col="ownership_code" sort={sort} onSort={onSort} />
                <SortHeader label="Status" col="status" sort={sort} onSort={onSort} />
                <Th>Dostęp</Th>
                <Th>Właściciele</Th>
              </tr>
            </thead>
            <tbody>
              {data.results.map((r) => (
                <tr
                  key={r.id}
                  className="border-t border-[var(--color-border-muted)] hover:bg-[var(--color-bg-elevated)]"
                >
                  <td className="px-3 py-2 font-mono whitespace-nowrap">
                    <span
                      className="inline-block h-2 w-2 rounded-full mr-1.5 align-middle"
                      style={{ backgroundColor: parcelDotColor(r.status) }}
                    />
                    <span title={[r.teryt, r.kw_number && `KW: ${r.kw_number}`].filter(Boolean).join("\n")}>
                      {r.short_number || r.teryt}
                    </span>
                    {r.is_manual && (
                      <span className="ml-1 text-[10px] text-[var(--color-text-muted)]">[ręczna]</span>
                    )}
                    {!r.in_current_route && (
                      <span className="ml-1 text-[10px] text-[var(--color-text-muted)]">[poza trasą]</span>
                    )}
                  </td>
                  <td className="px-3 py-2 max-w-[220px]">
                    {minimal ? (
                      <span
                        className="block truncate"
                        title={[r.project_name, r.branch_name && `Oddział: ${r.branch_name}`]
                          .filter(Boolean)
                          .join("\n")}
                      >
                        {r.project_name}
                      </span>
                    ) : (
                      <Link
                        to={`/power-projects/${r.project_id}?tab=parcels`}
                        className="text-[var(--color-primary)] hover:underline block truncate"
                        title={[r.project_name, r.branch_name && `Oddział: ${r.branch_name}`]
                          .filter(Boolean)
                          .join("\n")}
                      >
                        {r.project_name}
                      </Link>
                    )}
                  </td>
                  <td className="px-3 py-2 max-w-[140px] truncate" title={r.coordinators}>
                    {r.coordinators || "—"}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.county || "—"}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.commune || "—"}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.obreb || "—"}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap font-mono">
                    {fmtArea(r.area_m2)}
                  </td>
                  <td className="px-3 py-2"><LandClassCell row={r} /></td>
                  <td className="px-3 py-2 whitespace-nowrap" title="Kod własności (grupa rejestrowa)">
                    {r.ownership_code || "—"}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <ParcelStatusBadge status={r.status} />
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap" title={r.access_mode_display}>
                    <div>{r.access_mode_display}</div>
                    {r.owners_total > 0 && (
                      <div className="text-[10px] font-mono text-[var(--color-text-muted)]">
                        {r.owners_agreed}/{r.owners_total} rozstrzygniętych
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2"><OwnersCell row={r} /></td>
                </tr>
              ))}
            </tbody>
          </TableShell>

          {/* ── paginacja ── */}
          <div className="flex flex-wrap items-center gap-2 mt-3 text-xs font-mono text-[var(--color-text-muted)]">
            <span>
              {data.count} {data.count === 1 ? "działka" : "działek"} · strona {data.page} z {data.pages}
            </span>
            <div className="ml-auto flex items-center gap-2">
              <select
                className="input-base"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                title="Wierszy na stronę"
              >
                {PAGE_SIZES.map((s) => <option key={s} value={s}>{s} / str.</option>)}
              </select>
              <Button
                size="sm"
                variant="ghost"
                disabled={data.page <= 1 || list.isFetching}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ‹ Poprzednia
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={data.page >= data.pages || list.isFetching}
                onClick={() => setPage((p) => p + 1)}
              >
                Następna ›
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
